import { Geolocation } from '@capacitor/geolocation';
import { alpha, InputAdornment } from '@material-ui/core';
import { appEnv } from 'app/core/environment';
import Button from 'app/shared/components/button/Button';
import TextInput from 'app/shared/components/form-input/TextInput';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import React, { useState } from 'react';
import Geocode, { GeocodeSearchResult } from 'react-geocode';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import styled from 'styled-components/macro';

const StyledLocationSearch = styled.div`
  padding: ${styledFuncs.pad(2)} ${styledFuncs.pad(2)} 0;
  ${styledMixins.roundCorners};
`;
const GoogleSearchInput = styled(TextInput)`
  width: 100%;
  .MuiOutlinedInput-root {
    background-color: white;
    fieldset {
      top: -5px;
    }
  }
  .MuiOutlinedInput-input::placeholder {
    color: ${p => alpha(styledConsts.cPrimary(p), 0.4)} !important;
  }
`;
const SearchList = styled.ul`
  background: white;
  list-style: none;
  padding: 0;
  margin: ${styledFuncs.pad()} 0;
  ${styledMixins.roundCorners};
`;
const SearchListItem = styled.li`
  border-bottom: 1px solid #d4d1d1;
  padding: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)};
`;
type SearchedPlaceProps = {
  place_id: string;
  description: string;
};
type GooglePlacesTextboxProps = {
  onPlaceSelected: (res: GeocodeSearchResult) => void;
  setIsPlacesResultShowing?: (showing: boolean) => void;
};
export default function GooglePlacesTextbox({
  onPlaceSelected,
  setIsPlacesResultShowing,
}: GooglePlacesTextboxProps): JSX.Element {
  const { placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: appEnv.GOOGLE_API_KEY,
  });
  Geocode.setApiKey(appEnv.GOOGLE_API_KEY);
  const [selectedPlace, setSelectedPlace] = useState('');
  const showInfo = useShowInfo();
  const SearchPlaces = (val: string) => {
    setSelectedPlace(val);
    getPlacePredictions({ input: val });
    setIsPlacesResultShowing?.(!!val);
  };
  const setCurrentLocation = () => {
    Geolocation.getCurrentPosition()
      .then(position => {
        if (position?.coords?.latitude) {
          onPlaceSelected({
            geometry: {
              location: {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              },
            },
          });
        }
      })
      .catch(() => {
        showInfo.showSnack({
          message: "Couldn't fetch your location.",
        });
      });
  };
  const startAdornment = (
    <InputAdornment position="start">
      <Button onClick={() => SearchPlaces(selectedPlace)} iconButton>
        <Icon color="primary" icon={IconLib.SEARCH} size={24} />
      </Button>
    </InputAdornment>
  );
  const endAdornment = (
    <InputAdornment position="end">
      <Button onClick={setCurrentLocation} iconButton>
        <Icon color="primary" icon={IconLib.COMPASS} size={26} />
      </Button>
    </InputAdornment>
  );
  const selectPlace = (place: SearchedPlaceProps) => {
    setSelectedPlace(place.description);
    placePredictions.splice(0, placePredictions.length);
    Geocode.fromAddress(place.description).then(
      res => onPlaceSelected(res?.results[0]),
      () => {}
    );
  };
  return (
    <>
      <StyledLocationSearch>
        <GoogleSearchInput
          placeholder="Search location"
          value={selectedPlace}
          onChange={evt => SearchPlaces(evt.target.value)}
          InputProps={{
            startAdornment,
            endAdornment,
          }}
        />
        <SearchList>
          {placePredictions.map((place: SearchedPlaceProps) => (
            <SearchListItem
              key={place.place_id}
              onClick={() => selectPlace(place)}
            >
              {place.description}
            </SearchListItem>
          ))}
        </SearchList>
      </StyledLocationSearch>
    </>
  );
}
